// agendaApp.ts
import * as SRACommon from '@swbc/devops-send-release-agenda-common';

// --- Interfaces (Define based on expected data structure from API and Agenda.html) ---
interface relations {
    rel: string; // Relation type (e.g., "Hyperlink", "ArtifactLink")
    url: string; // URL to the related item
}

interface WorkItem {
    id: number;
    fields: {
        'System.Id': number;
        'System.WorkItemType': string;
        'System.Title': string;
        'System.TeamProject': string;
        'SWBC.Department'?: string; // Example custom field
        'SWBC.Application'?: string;
        'SWBC.Risk'?: string;
        'Microsoft.VSTS.Common.DD'?: string; // Deployment Date
        'Microsoft.VSTS.Common.ImpDate'?: string; // Implementation Date
        'SWBC.RelatedTasks'?: string;
        'SWBC.DevMan'?: { displayName?: string };
        'SWBC.Developer'?: { displayName?: string };
        'SWBC.Tester'?: { displayName?: string };
        'SWBC.AutoStart'?: string; // DevOps Attendance
        'SWBC.AS400Task'?: string;
        'SWBC.ApplicationType'?: string;
        'SWBC.AS400.DeployTime'?: string;
        'Custom.YAMLReleasePipelineLink'?: string; // For YAML releases
        'Custom.YAMLReleaseName'?: string;

        // Add other relevant fields from the PS script queries
    };
    relations: relations[]; // Array of relations for the work item
    url: string; // API URL for the work item
}



interface ReleaseItem { // Structure matching the properties used in Agenda.html {{#each Releases}} block
    Department: string; // Department name
    Type: string; // 'R', 'PBI', 'US', 'B' etc.
    WID: number;
    DD: Date;
    WorkItemUrl?: string; // Link to ADO item
    ReleaseName: string;
    Release?: string | null; // Link to ADO release/build
    Application: string;
    TeamProject: string;
    Description: string;
    DeploymentType: string; // Example: 'N/A' if not applicable
    Risk: string;
    DDHumanFormat: string; // Formatted Date/Time string
    RelatedTasks: string;
    DevMan: string;
    Developer?: string;
    Tester: string;
    RMAttendance: string; // DevOps Attendance required?
    BuildId?: number; // For classic releases
    lpo?: boolean; // Flags for different orgs/projects if needed
    suretrack?: boolean;
    lendone?: boolean;
}

interface DepartmentData {
    DepartmentName: string;
    Releases: ReleaseItem[];
}

interface AgendaData { // Structure matching top-level object in Agenda.html
    Subject: string;
    OnCall: string;
    OnCallNextWeek: string;
    DepartmentReleaseSet: DepartmentData[];
    Signature: string; // HTML signature content
    stylesDoNotInline: string; // Placeholder for CSS styles if needed
}

interface TechNotifyData { // Structure matching top-level object in TechNotify.html
    Subject: string;
    Releases: ReleaseItem[]; // Array of release items for Tech Notify
    Signature: string; // HTML signature content
    stylesDoNotInline: string; // Placeholder for CSS styles if needed
}

interface CoordData { // Structure matching top-level object in TechNotify.html
    Subject: string;
    Releases: ReleaseItem[]; // Array of release items for Tech Notify
    OnCall: string; // Who is sending the notification
    Signature: string; // HTML signature content
    stylesDoNotInline: string; // Placeholder for CSS styles if needed
}

// --- Interfaces for ADO API Response (subset) ---
interface AdoClassicReleaseRef {
    id: number;
    name: string;
    url: string; // API URL for the release details
    _links?: { // Usually present, contains web URL
        web?: {
            href: string;
        };
    };
    // Add other potentially useful fields like status, createdOn if needed
}

interface AdoClassicReleaseListResponse {
    count: number;
    value: AdoClassicReleaseRef[];
}

interface FoundReleaseInfo {
    releaseId: number;
    releaseName: string;
    releaseUrl: string; // This should be the *web* URL for user linking
}

// --- Configuration (Replace with your actual details) ---
const ADO_CONFIG = {
    orgUrl: "https://dev.azure.com//SWBC-FigWebDev", // e.g., https://dev.azure.com/SWBC-FigWebDev
    // Separate configs might be needed for FigLpo, FigSure-Track if they use different PATs/Orgs
    lpoOrgUrl: "https://dev.azure.com/SWBC-FigLpo",
    suretrackOrgUrl: "https://dev.azure.com/SWBC-FIGSure-Track",
    // !! IMPORTANT: Handle PAT securely. DO NOT hardcode here in production client-side code. !!
    // Consider a backend proxy or secure authentication flow.
    pat: "EXGnTzrv7KKuCQH3VeXaeLRMo0q2XDTxTttkTIHHDRVWsYMkmVCxJQQJ99BCACAAAAAScRVNAAAGAZDO1EVu"
};

// --- DOM Elements ---
const dateFromInput = document.getElementById('dateFrom') as HTMLInputElement; // this is a date input field
const dateToInput = document.getElementById('dateTo') as HTMLInputElement; // this is a date input field
const refreshButton = document.getElementById('refreshButton') as HTMLButtonElement;
const agendaOutputDiv = document.getElementById('agendaOutput') as HTMLDivElement;
const techNotifyOutputDiv = document.getElementById('techNotifyOutput') as HTMLDivElement;
const coordOutputDiv = document.getElementById('coordOutput') as HTMLDivElement;
const timeRetrieved = document.getElementById('timeRetrieved') as HTMLInputElement; // this is a time field
const onCallOverride = document.getElementById('On-Call') as HTMLInputElement;
const onCallNextWeekOverride = document.getElementById('On-CallNextWeek') as HTMLInputElement;
const copyAgendaButton = document.getElementById('copyAgendaButton') as HTMLButtonElement;
const copyTechButton = document.getElementById('copyTechButton') as HTMLButtonElement;
const copyCoordButton = document.getElementById('copyCoordButton') as HTMLButtonElement;
const agendaSelectionButton = document.getElementById('buttonAgendaSelection') as HTMLButtonElement;
const agendaSelectionInput = document.getElementById('agendaSelectionField') as HTMLInputElement;
const techSelectionButton = document.getElementById('buttonTechnotifySelection') as HTMLButtonElement;
const techSelectionInput = document.getElementById('technotifySelectionField') as HTMLInputElement;
const alertDiv = document.getElementById('alert') as HTMLDivElement

const jokes = [
    "Why did the developer go broke? Because he used up all his cache.",
    "Why do programmers prefer dark mode? Because light attracts bugs.",
    "Why do Java developers wear glasses? Because they don't see sharp.",
    "How many programmers does it take to change a light bulb? None. It's a hardware problem.",
    "How many DevOps engineers does it take to change a light bulb? None. They just automate the process.",
    "If it's working on your local...bad news, friend. We're shipping your local.",
    "I did a survey of programmers. 100% of them said they don't like surveys.",
    "I did a survey of other companies' cloud strategies. 'Hope and Pray' was the most common answer.",
    "What's the most used language in programming? Profanity.",
    "DevOps Borat say: for every stakeholder involve in your project is completion of project delay by six month.",
    "Why do DevOps engineers prefer dark mode? Because light attracts bugs!"
]


function getRandomJoke(): string {
    const randomIndex = Math.floor(Math.random() * jokes.length);
    return jokes[randomIndex];
}

// Function to update the URL with query parameters
function updateURLWithDates(dateFrom: string, dateTo: string): void {
    const url = new URL(window.location.href);
    url.searchParams.set('dateFrom', dateFrom);
    url.searchParams.set('dateTo', dateTo);
    window.history.replaceState({}, '', url.toString());
}

// Function to read query parameters and set input values
function setDatesFromURL(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const dateFrom = urlParams.get('dateFrom');
    const dateTo = urlParams.get('dateTo');

    if (dateFrom) {
        dateFromInput.value = dateFrom;
    }
    if (dateTo) {
        dateToInput.value = dateTo;
    }

    // Adjust DateTo if DateFrom is set and DateTo is missing
    if (dateFrom && !dateTo) {
        adjustFromDateBasedOnToDate(dateFrom);
    }
}

// Function to implement business rule 'look ahead' logic for the DateTo, if given DateFrom
// This function adjusts the date based on the day of the week, similar to the PowerShell logic
function adjustFromDateBasedOnToDate(inputDate: string): void {
    // console.log(`Adjusting date from ${inputDate}`); // Debugging output
    const adjustedDate = new Date(inputDate + 'T18:00:00Z'); // Parse the date string to a Date object
    const dayOfWeek = adjustedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    let daysToAdd = 0; // Default to adding 1 day
    // console.log(`Input date: ${inputDate}, Day of week: ${dayOfWeek}`); // Debugging output
    if (dayOfWeek === 4) { // Thursday
        daysToAdd = 6;
    } else if (dayOfWeek === 5) { // Friday
        daysToAdd = 2;
    }
    console.log(`Adjusting date by ${daysToAdd} days`); // Debugging output

    
    adjustedDate.setDate(adjustedDate.getDate() + daysToAdd);
    dateToInput.value = adjustedDate.toISOString().split('T')[0];
}

async function pasteRich(rich, plain) {
    if (typeof ClipboardItem !== "undefined") {
      // Shiny new Clipboard API, not fully supported in Firefox.
      // https://developer.mozilla.org/en-US/docs/Web/API/Clipboard_API#browser_compatibility
      const html = new Blob([rich], { type: "text/html" });
      const text = new Blob([plain], { type: "text/plain" });
      const data = new ClipboardItem({ "text/html": html, "text/plain": text });
      await navigator.clipboard.write([data]);
    } else {
      // Fallback using the deprecated `document.execCommand`.
      // https://developer.mozilla.org/en-US/docs/Web/API/Document/execCommand#browser_compatibility
      const cb = e => {
        e.clipboardData.setData("text/html", rich);
        e.clipboardData.setData("text/plain", plain);
        e.preventDefault();
      };
      document.addEventListener("copy", cb);
      document.execCommand("copy");
      document.removeEventListener("copy", cb);
    }
  }

function showTooltip(element: HTMLElement, message: string): void {
    // Add tooltip attributes to the element
    element.setAttribute("data-bs-toggle", "tooltip");
    element.setAttribute("data-bs-placement", "top");
    element.setAttribute("data-bs-title", message);

    // Initialize the tooltip (Bootstrap requires this)
    const tooltip = new bootstrap.Tooltip(element);
    tooltip.show();

    // Optionally hide the tooltip after a short delay
    setTimeout(() => {
        tooltip.dispose();
    }, 5000); // Tooltip will disappear after 5 seconds
}

async function copyOutput(agendaOutputDiv: HTMLElement, copyAgendaButton: HTMLInputElement): Promise<void> {
    if (agendaOutputDiv && copyAgendaButton) {
        // Clone the agendaOutputDiv to avoid modifying the original
        const clonedDiv = agendaOutputDiv.cloneNode(true) as HTMLElement;
        pasteRich(clonedDiv.innerHTML, null).then(() => {
            showTooltip(copyAgendaButton,"Successfully copied agenda!")
        }).catch(err => {
            console.error('Failed to copy agenda:', err);
            alert('Failed to copy agenda. Please try again.');
        });
    }
}

async function sendEmail(
    from: string,
    to: string,
    subject: string,
    emailBody: HTMLElement,
    type: string
): Promise<void> {
    const apiUrl = "https://zz1tan0peh.execute-api.us-east-1.amazonaws.com/production/email";

    try {
        // Prepare the payload
        const payload = {
            from,
            to,
            subject,
            emailBody: emailBody.innerHTML, // Extract the HTML content of the element
            type,
        };

        // Make the PUT request
        const response = await fetch(apiUrl, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        // Parse the response
        const returnData = await response.json();

        // Get the current time
        const currentTime = new Date().toLocaleTimeString("en-US", { hour12: false });

        if (response.ok) {
            // On success, display a tooltip with success message
            const successMessage = `${type} email from ${from} to ${to} processed and sent successfully at ${currentTime}. HTML output saved to ${returnData.s3Key}`;
            appendAlert(successMessage,'success');
            showTooltip(emailBody, successMessage);
        } else {
            // On failure, display a tooltip with error message
            const errorMessage = `${type} email from ${from} to ${to} failed; error log is "${returnData.message || JSON.stringify(returnData)}"`;
            appendAlert(errorMessage,'danger')
            // showTooltip(emailBody, errorMessage);
        }
    } catch (error) {
        // Handle network or unexpected errors
        const errorMessage = `${type} email from ${from} to ${to} failed; error log is "${error.message}"`;
        alertDiv.innerHTML += `<div class="alert alert-danger" role="alert">${errorMessage}</div>`;
        // showTooltip(emailBody, errorMessage);
    }
}

function agendaSubject(): string {
        // console.log(`dateFromInput.value is ${dateFromInput.value}`)
        // console.log(`dateToInput.value is ${dateFromInput.value}`)
        if (dateFromInput.value == dateToInput.value) {return `Deployment Agenda - ${new Date(dateFromInput.value + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}, ${dateFromInput.value}`}
        else {return `Deployment Agenda - ${new Date(dateFromInput.value + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}, ${dateFromInput.value} to ${new Date(dateToInput.value + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}, ${dateToInput.value}`;}
}

function techSubject(): string {
    return `Deployments Starting - ${new Date(dateFromInput.value + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' })}, ${dateFromInput.value}`
}

function appendAlert (message:string, type:string): void {
    const wrapper = document.createElement('div')
    wrapper.innerHTML = [
      `<div class="alert alert-${type} alert-dismissible" role="alert">`,
      `   <div>${message}</div>`,
      '   <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>',
      '</div>'
    ].join('')
  
    alertDiv.append(wrapper)
  }
// --- Main Application Logic ---

async function loadAndRenderAgenda() {
    if (!agendaOutputDiv) return;
    if (!techNotifyOutputDiv) return;
    if (!coordOutputDiv) return;
    if (dateFromInput.value > dateToInput.value) {
        agendaOutputDiv.innerHTML = `<p style="color: red;">Hey smart guy, you got your dates crossed - 'From' can't be greater than 'To'</p>`;
        techNotifyOutputDiv.innerHTML = `<p style="color: red;">Date From must be less than Date To.</p>`;
        return
    }
    copyAgendaButton.disabled = true;
    copyTechButton.disabled = true;
    copyCoordButton.disabled = true;
    agendaOutputDiv.innerHTML = `Loading...this might take a few seconds, depending on the number of releases.<br><br>${getRandomJoke()}`;
    techNotifyOutputDiv.innerHTML = 'Loading...this might take a few seconds, depending on the number of releases.';
    coordOutputDiv.innerHTML = `Loading...this might take a few seconds, depending on the number of releases.`;

    // Event listener to update URL when date inputs change
    dateFromInput?.addEventListener('change', () => {
        adjustFromDateBasedOnToDate(dateFromInput.value);
        updateURLWithDates(dateFromInput.value, dateToInput.value);
    });

    const dateFrom = dateFromInput.value || new Date(new Date().toLocaleDateString('en-US', { timeZone: 'America/Chicago' })).toISOString().split('T')[0]; // Default to today if empty
    const dateTo = dateToInput.value || dateFrom; // Default to dateFrom if empty
    const currentTime = new Date().toLocaleTimeString('en-US', { hour12: false }); // Format as HH:mm:ss
    timeRetrieved.value = currentTime;
    console.log(`Fetching data for date: ${dateFrom} at ${currentTime}`); // Debugging output

    // Render the agenda with the final data
        
    const agendaTemplateString = document.getElementById("agendaTemplate")?.innerHTML || ""; // Assuming template is in a <script> tag with id="agendaTemplate"
    const templateTechNotifyString = document.getElementById("techNotifyTemplate")?.innerHTML || ""; // Assuming template is in a <script> tag with id="techNotifyTemplate"
    const templateCoordString = document.getElementById("coordTemplate")?.innerHTML || ""; // Assuming template is in a <script> tag with id="coordTemplate"
    const style = document.getElementById("stylescss")?.innerHTML || "";
    const stylesDoNotInline = document.getElementById("doNotInline")?.innerHTML || "";
    const signature = document.getElementById("agendaSignature")?.innerHTML || "";
    const packageOutput = await SRACommon.loadAndRenderAgenda(dateFromInput.value,dateToInput.value,onCallOverride.value,onCallNextWeekOverride.value,ADO_CONFIG,agendaTemplateString,templateTechNotifyString,templateCoordString,signature,stylesDoNotInline,style)
        // const packageOutputAgendaDiv = document.getElementById('packageOutputAgenda') as HTMLInputElement;
        // const packageOutputTechDiv =document.getElementById('packageOutputTech') as HTMLInputElement;
        // const packageOutputCoordDiv = document.getElementById('packageOutputCoord') as HTMLInputElement;
    agendaOutputDiv.innerHTML = packageOutput[0];
    copyAgendaButton.disabled = false;
    if (!copyAgendaButton.hasAttribute('data-listener-added')) {
        copyAgendaButton.addEventListener('click', () => {
            copyOutput(agendaOutputDiv, copyAgendaButton);
        });
        // Mark the button to indicate the listener has been added
        copyAgendaButton.setAttribute('data-listener-added', 'true');
    }
    techNotifyOutputDiv.innerHTML = packageOutput[1];
    copyTechButton.disabled = false;
    if (!copyTechButton.hasAttribute('data-listener-added')) {
        copyTechButton.addEventListener('click', () => {
            copyOutput(techNotifyOutputDiv, copyTechButton);
        });
        // Mark the button to indicate the listener has been added
        copyTechButton.setAttribute('data-listener-added', 'true');
    }
    coordOutputDiv.innerHTML = packageOutput[2];
    copyCoordButton.disabled = false;
    if (!copyCoordButton.hasAttribute('data-listener-added')) {
        copyCoordButton.addEventListener('click', () => {
            copyOutput(coordOutputDiv, copyCoordButton);
        });
        // Mark the button to indicate the listener has been added
        copyCoordButton.setAttribute('data-listener-added', 'true');
    }

    const rwisOutput = document.getElementById('rwisOutputReport') as HTMLInputElement
    rwisOutput.innerHTML = `<div class="text-center"><div class="spinner-border" style="width: 5rem; height: 5rem;" role="status"><span class="visually-hidden">Loading...</span></div><br>Loading...this might take a couple of minutes, depending on the number of releases.<br><br>${getRandomJoke()}</div>`
    rwisOutput.innerHTML = await SRACommon.generateReport(ADO_CONFIG,dateFromInput.value)
    // alertDiv.innerHTML += `${agendaSubject()}`
    // let agendaSubjectForEmail = agendaSubject()
    // sendEmail("edward.mccormick@swbc.com","edward.mccormick@swbc.com", agendaSubjectForEmail, agendaOutputDiv,"agenda")

}



dateToInput?.addEventListener('change', () => {
    updateURLWithDates(dateFromInput.value, dateToInput.value);
});

// --- Initial Load ---
// Set default date to today
const today = new Date(new Date().toLocaleDateString('en-US', { timeZone: 'America/Chicago' })).toISOString().split('T')[0];
console.log(`Default date set to: ${today}`); // Debugging output
if (dateFromInput) {
    dateFromInput.value = today;
    adjustFromDateBasedOnToDate(today); // Adjust DateTo based on default DateFrom
}
window.onload = () => {

    setDatesFromURL(); // Set dates from URL if available
    refreshButton?.addEventListener('click', loadAndRenderAgenda)
    loadAndRenderAgenda(); 
    agendaSelectionButton.addEventListener('click', () => {
        sendEmail("edward.mccormick@swbc.com",agendaSelectionInput.value, agendaSubject(), agendaOutputDiv,"agenda")
    })
    techSelectionButton.addEventListener('click', () => {
        sendEmail("edward.mccormick@swbc.com",techSelectionInput.value, techSubject(), techNotifyOutputDiv,"technotify")
    })
    // alertDiv.innerHTML += `<div class="alert alert-primary" role="alert">${agendaSubject()}</div>`


}// Load data on page load